import { Form, Formik } from 'formik';
import React, { FC, useContext, useState } from 'react';
import * as yup from 'yup';
import { UpdateDetailsFormType } from '../../../models/update-details-form-type';
import FormErrorTypeEnum from '../../../models/form-error-type-enum';
import accountUpdate from '../../../services/customer-api/endpoints/account/update';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import FormText from '../../atoms/form-text/form-text';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import updateUser from '../../providers/user/actions/update-user';
import {
  UserDispatchContext,
  UserStateContext,
} from '../../providers/user/user.provider';
import usePushFormikFormToDataLayer from '../../../hooks/use-push-formik-form-to-data-layer/use-push-formik-form-to-data-layer';

interface Props extends UpdateDetailsFormType {
  readonly onSuccess: () => void;
}

const UpdateDetailsForm: FC<Props> = ({
  emailAddressFieldLabel,
  emailAddressFieldMandatoryError,
  emailAddressFieldValidationError,
  emailAddressNotUnique,
  firstNameFieldLabel,
  firstNameFieldMandatoryError,
  lastNameFieldLabel,
  lastNameFieldMandatoryError,
  onSuccess,
  saveButtonText,
}: Props) => {
  let { user } = useContext(UserStateContext);
  const userDispatch = useContext(UserDispatchContext);
  const [formSuccessfullySubmitted, setFormSuccessfullySubmitted] =
    useState<boolean>(null);

  const formContext = usePushFormikFormToDataLayer(
    'Update details',
    formSuccessfullySubmitted
  );

  if (!user) {
    return <LoadingSpinner className="h-96" />;
  }

  return (
    <Formik
      innerRef={formContext}
      initialValues={{
        firstName: user.firstName,
        lastName: user.lastName,
        emailAddress: user.email,
      }}
      validationSchema={yup.object({
        firstName: yup
          .string()
          .required(firstNameFieldMandatoryError)
          .trim(firstNameFieldMandatoryError)
          .label(firstNameFieldLabel),
        lastName: yup
          .string()
          .required(lastNameFieldMandatoryError)
          .trim(lastNameFieldMandatoryError)
          .label(lastNameFieldLabel),
        emailAddress: yup
          .string()
          .email(emailAddressFieldValidationError)
          .required(emailAddressFieldMandatoryError)
          .label(emailAddressFieldLabel),
      })}
      validateOnMount
      validateOnChange
      onSubmit={async (values, { setErrors }) => {
        user = {
          ...user,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.emailAddress,
        };

        try {
          await accountUpdate(user);

          userDispatch(updateUser(user));
          setFormSuccessfullySubmitted(true);
          onSuccess();
        } catch (err) {
          if (
            err.response.status === 400 &&
            err.response.data.message === FormErrorTypeEnum.EmailNotUnique
          ) {
            setErrors({
              emailAddress: emailAddressNotUnique,
            });
          } else {
            setFormSuccessfullySubmitted(false);
          }
        }
      }}
    >
      {({ initialValues, isValid, values }) => {
        const formChanged =
          initialValues.firstName !== values.firstName ||
          initialValues.lastName !== values.lastName ||
          initialValues.emailAddress !== values.emailAddress;

        return (
          <Form className="flex flex-col space-y-5">
            <FormText label={firstNameFieldLabel} name="firstName" required />

            <FormText label={lastNameFieldLabel} name="lastName" required />

            <FormText
              label={emailAddressFieldLabel}
              name="emailAddress"
              required
            />

            {formSuccessfullySubmitted === false && (
              <span className="block mt-1 text-sm text-invalid">
                {FormErrorTypeEnum.Unknown}
              </span>
            )}

            <div className="flex justify-center py-component-spacing-mobile lg:py-component-spacing-content">
              <ButtonPrimary
                buttonType="submit"
                disabled={!isValid || !formChanged}
                name={saveButtonText}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default UpdateDetailsForm;
